import { useState, useRef, useEffect } from 'react'
import { useAppStore }                  from '@/stores/appStore'
import { fetchFileContent }             from '@/lib/github'
import {
  downloadTroveAsDocx,
  downloadTroveAsMarkdown,
  downloadTroveAsText,
  baseFilename,
} from '@/lib/download'

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg className={`w-3 h-3 text-forge-ash transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

// ─── Trove format options ─────────────────────────────────────────────────────

interface TroveFormat {
  id:    'docx' | 'md' | 'txt'
  label: string
  ext:   string
}

const TROVE_FORMATS: TroveFormat[] = [
  { id: 'docx', label: 'Word',       ext: '.docx' },
  { id: 'md',   label: 'Markdown',   ext: '.md'   },
  { id: 'txt',  label: 'Plain Text', ext: '.txt'  },
]

// ─── ExPedition Panel ─────────────────────────────────────────────────────────

export function ExPedition() {
  const {
    expedition,
    expeditionName,
    selectedRepo,
    removeFromExpedition,
    reorderExpedition,
    clearExpedition,
    setExpeditionName,
  } = useAppStore()

  const [open,        setOpen]        = useState(true)
  const [menuOpen,    setMenuOpen]    = useState(false)
  const [busy,        setBusy]        = useState<string | null>(null)
  const [error,       setError]       = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close export menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  // Panel is hidden when nothing's been added
  if (expedition.length === 0) return null

  const handleExport = async (fmt: TroveFormat) => {
    if (!selectedRepo) return
    setBusy(fmt.id)
    setError(null)
    setMenuOpen(false)
    try {
      const results = await Promise.all(
        expedition.map((f) =>
          fetchFileContent(selectedRepo.owner, selectedRepo.name, f.sha, f.path)
        )
      )
      const files = results.map((r, i) => ({
        content: r.content,
        name:    baseFilename(expedition[i].path),
      }))
      const title = expeditionName.trim() || selectedRepo.name
      switch (fmt.id) {
        case 'docx': await downloadTroveAsDocx(files, title);     break
        case 'md':        downloadTroveAsMarkdown(files, title);   break
        case 'txt':       downloadTroveAsText(files, title);       break
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="border-t border-forge-border flex-shrink-0 bg-forge-void">

      {/* ── Header ── */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-forge-slate/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {/* Compass/map icon — expedition feel */}
          <svg className="w-3.5 h-3.5 text-forge-gold flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <span className="text-xs font-semibold text-forge-gold uppercase tracking-wider">
            ExPedition
          </span>
          <span className="text-[10px] bg-forge-gold/20 text-forge-gold rounded-full px-1.5 py-0.5 font-mono leading-none">
            {expedition.length}
          </span>
        </div>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className="pb-3">

          {/* ── Name field ── */}
          <div className="px-3 pb-2">
            <input
              type="text"
              value={expeditionName}
              onChange={(e) => setExpeditionName(e.target.value)}
              placeholder={`${selectedRepo?.name ?? 'Trove'} name…`}
              className="w-full bg-forge-obsidian border border-forge-border rounded-md px-2.5 py-1.5
                         text-xs text-forge-primary placeholder-forge-ash outline-none
                         focus:border-forge-gold/50 transition-colors"
            />
          </div>

          {/* ── File list ── */}
          <div className="max-h-44 overflow-y-auto px-2 space-y-0.5">
            {expedition.map((file, idx) => (
              <div
                key={file.path}
                className="flex items-center gap-1 px-1 py-1 rounded-md group hover:bg-forge-slate/40 transition-colors"
              >
                {/* Order controls */}
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => reorderExpedition(idx, idx - 1)}
                    disabled={idx === 0}
                    className="text-forge-ash hover:text-forge-gold disabled:opacity-20 transition-colors leading-none"
                    title="Move up"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => reorderExpedition(idx, idx + 1)}
                    disabled={idx === expedition.length - 1}
                    className="text-forge-ash hover:text-forge-gold disabled:opacity-20 transition-colors leading-none"
                    title="Move down"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Position index */}
                <span className="text-[10px] text-forge-ash font-mono w-4 text-center flex-shrink-0">
                  {idx + 1}
                </span>

                {/* Filename */}
                <span
                  className="text-xs text-forge-primary flex-1 truncate"
                  title={file.path}
                >
                  {file.name}
                </span>

                {/* Remove */}
                <button
                  onClick={() => removeFromExpedition(file.path)}
                  className="text-forge-ash hover:text-forge-heat-critical opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                  title="Remove from ExPedition"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          {/* ── Error ── */}
          {error && (
            <p className="px-3 pt-2 text-[11px] text-forge-heat-critical leading-relaxed">{error}</p>
          )}

          {/* ── Actions ── */}
          <div className="px-3 pt-2.5 flex items-center gap-2">

            {/* Export Trove — split button with format dropdown */}
            <div ref={menuRef} className="flex-1 relative">
              <div className="flex rounded-lg overflow-visible border border-forge-gold/30 bg-forge-gold/15">
                {/* Primary action — last-used or default (Word) */}
                <button
                  onClick={() => void handleExport(TROVE_FORMATS[0])}
                  disabled={!!busy}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5
                             text-xs font-semibold text-forge-gold
                             hover:bg-forge-gold/15
                             disabled:opacity-50 transition-colors"
                >
                  {busy ? <Spinner /> : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  )}
                  Export Trove
                </button>

                {/* Chevron — opens format picker */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  disabled={!!busy}
                  className="px-2 py-1.5 border-l border-forge-gold/30 text-forge-gold
                             hover:bg-forge-gold/15 disabled:opacity-50 transition-colors"
                  title="Choose export format"
                >
                  <svg className={`w-3 h-3 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Format dropdown — opens upward so it doesn't get clipped */}
              {menuOpen && (
                <div className="absolute bottom-full mb-1.5 left-0 right-0
                                bg-forge-basalt border border-forge-border rounded-lg
                                shadow-2xl shadow-black/50 z-50 overflow-hidden">
                  {TROVE_FORMATS.map((fmt) => (
                    <button
                      key={fmt.id}
                      onClick={() => void handleExport(fmt)}
                      disabled={!!busy}
                      className="w-full flex items-center justify-between px-3 py-2
                                 text-xs text-forge-primary hover:bg-forge-slate
                                 disabled:opacity-50 transition-colors"
                    >
                      <span>{fmt.label}</span>
                      <span className="text-forge-ash font-mono">
                        {busy === fmt.id ? <Spinner /> : fmt.ext}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={clearExpedition}
              disabled={!!busy}
              className="px-2.5 py-1.5 rounded-lg text-xs text-forge-ash border border-forge-border
                         hover:text-forge-primary hover:border-forge-slate disabled:opacity-50 transition-colors"
              title="Clear ExPedition"
            >
              Clear
            </button>
          </div>

        </div>
      )}
    </div>
  )
}
